import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "bg-ink-900/60 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-blaze-blueLight h-11";

export function TestimonialForm({ onSuccess }) {
  const [form, setForm] = useState({ name: "", role: "", rating: 5, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/testimonials", form);
      setDone(true);
      toast.success("Thank you!", { description: data.message });
      onSuccess?.();
    } catch (err) {
      toast.error("Could not submit", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="testimonial-success" className="text-center py-10">
        <CheckCircle2 className="mx-auto text-blaze-orange" size={54} />
        <h3 className="mt-4 font-heading text-3xl tracking-wide text-white">Thank You!</h3>
        <p className="mt-2 text-white/70 max-w-md mx-auto">
          Your testimonial was submitted and will appear on the site once approved.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="testimonial-form" className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Your Name *</Label>
          <Input required data-testid="testimonial-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Role</Label>
          <Input data-testid="testimonial-role" className={inputCls} value={form.role} onChange={set("role")} placeholder="e.g. Parent of 12U player" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-white/80 text-xs uppercase tracking-wider">Rating</Label>
        <div className="flex items-center gap-2" data-testid="testimonial-rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              type="button"
              key={n}
              data-testid={`testimonial-star-${n}`}
              onClick={() => setForm((f) => ({ ...f, rating: n }))}
              className="transition-transform hover:scale-110"
              aria-label={`${n} star`}
            >
              <Star size={26} className={n <= form.rating ? "fill-blaze-orange text-blaze-orange" : "text-white/25"} />
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-white/80 text-xs uppercase tracking-wider">Your Experience *</Label>
        <Textarea required data-testid="testimonial-message" className="bg-ink-900/60 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-blaze-blueLight min-h-[120px]" value={form.message} onChange={set("message")} placeholder="Share your experience with CT BLAZERS." />
      </div>
      <Button
        type="submit"
        disabled={loading}
        data-testid="testimonial-submit"
        className="w-full bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12 text-base shadow-[0_8px_28px_rgba(246,140,31,0.35)] transition-all"
      >
        {loading ? <Loader2 className="animate-spin" /> : "Submit Testimonial"}
      </Button>
    </form>
  );
}
